import { defineComponent as _defineComponent } from 'vue'
import type { GlobalModuleName } from '@/helpers/composables/useGlobalTitles';
import type { SportLocale } from '@/enums/sports';

import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import PictureComp from '@/components/picture/PictureComp.vue';
import CompetitionLogo from '@/components/sports/common/competition/logos/CompetitionLogo.vue';
import GlobalSportsV2Cards from './GlobalSportsV2Cards.vue';

import {
	GetCompetitionMatchCountDocument,
	type GetCompetitionMatchCountQuery,
	type GetCompetitionMatchCountQueryVariables,
} from '@/pages/graphql/queries/GetCompetitionMatchCountV2.query';

import { ObjectType, SportEventAggregation, SportType } from '@/@types/graphql-types';
import { SnowplowModuleContext, SnowplowSportContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { WebLocale } from '@/enums/web-locale';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { getOverviewUrlBySport } from '@/routing/sports';
import { useI18n } from '@/helpers/composables/useI18n';
import { useQuery } from '@/helpers/composables/useApollo';
import { computed } from 'vue';
import { useRoute } from 'vue-router/composables';
import { ImpressionTrackingEvents } from '@/enums/events';
import { defaultObservableOptions } from '@/mixins/types/impressionTracking';
import { usePersistedTodaysDate } from '@/helpers/composables/usePersistedTodaysDate';

type CompetitionEdge = NonNullable<GetCompetitionMatchCountQuery['sportCompetitionsV2']['edges']>[number];

export default /*#__PURE__*/_defineComponent({
  __name: 'GlobalSports',
  props: {
    language: null,
    country: null,
    webLocale: null,
    moduleName: null
  },
  setup(__props: any) {

const props = __props as {
	language: string;
	country: string;
	webLocale: SportLocale;
	moduleName: GlobalModuleName;
};



const backdropImage = `/${ASSETS_DIR}/img/home/global-sports-hero-1-comp.png`;

const route = useRoute();
const { todayDate } = usePersistedTodaysDate();

const queryVariables = computed(() => {
	return {
		country: props.country,
		first: 6,
		after: '',
		filter: {
			activeOn: todayDate.value.toISOString(),
		},
		language: props.language,
		groupBy: SportEventAggregation.Sport,
		timezoneOffset: 0,
	};
});

const enableQuery = computed(() => route.name === 'app.home');
const { result: competitionMatchCountData } = useQuery<
	GetCompetitionMatchCountQuery,
	GetCompetitionMatchCountQueryVariables
>(GetCompetitionMatchCountDocument, queryVariables, { enabled: enableQuery });

const competitionMatchCounts = computed(() => competitionMatchCountData.value?.sportCompetitionsV2?.edges);

function toCompetititionColumn(comp: CompetitionEdge) {
	return {
		node: comp?.node,
		id: comp?.node?.id,
		name: comp?.node?.content?.name,
		matchCount: comp?.node?.sportEventBucketsV2?.edges?.[0]?.node?.totalCount ?? 0,
		sport: comp?.node?.sport,
	};
}

const leftColumnCompetitions = computed(() =>
	competitionMatchCounts.value?.slice(0, 3)?.map(comp => toCompetititionColumn(comp))
);

const rightColumnCompetitions = computed(() =>
	competitionMatchCounts.value?.slice(3)?.map(comp => toCompetititionColumn(comp))
);

const sportOrder = computed(() => {
	switch (props.webLocale) {
		case WebLocale.us:
			return [
				SportType.AmericanFootball,
				SportType.Football,
				SportType.Tennis,
				SportType.Nascar,
				SportType.FormulaRacing,
				SportType.Baseball,
				SportType.Basketball,
				SportType.IceHockey,
			];
		default:
			return [
				SportType.Football,
				SportType.Tennis,
				SportType.FormulaRacing,
				SportType.AmericanFootball,
				SportType.Baseball,
				SportType.Basketball,
				SportType.IceHockey,
				SportType.Nascar,
			];
	}
});

const { t } = useI18n();
function getMatchCount(count: number, sport: SportType) {
	if (sport === SportType.FormulaRacing) {
		// Need to divide F1 races by 5 and round up since each GP comprises of 5 child events
		return t('WEBAPP_GLOBAL_RACES', { count: Math.ceil(count / 5) });
	}

	if (sport === SportType.Nascar) {
		return t('WEBAPP_GLOBAL_RACES', { count });
	}

	return t('WEBAPP_GLOBAL_MATCHES', { count });
}

const moduleContext = computed(() => new SnowplowModuleContext('', -1, props.moduleName, ''));
const pageTypeContext = computed(() => TrackingHelper.getPageTypeContext(true));

function handleSportClick(sport: SportType) {
	const sportContext = new SnowplowSportContext(sport, ObjectType.SportCompetition);

	TrackingHelper.trackEvent('userinteraction', { action: 'sport_clicked', useLastRoute: true }, [
		sportContext,
		pageTypeContext.value,
		moduleContext.value,
	]);
}

const observeVisibilityOptions = computed(() => ({
	...defaultObservableOptions,
	callback: (isVisible: boolean) => {
		if (!isVisible) return;

		TrackingHelper.trackEvent(
			ImpressionTrackingEvents.MODULE,
			{
				action: 'impression',
				nonInteraction: true,
			},
			[moduleContext.value]
		);
	},
}));

return { __sfc: true,props, backdropImage, route, todayDate, queryVariables, enableQuery, competitionMatchCountData, competitionMatchCounts, toCompetititionColumn, leftColumnCompetitions, rightColumnCompetitions, sportOrder, t, getMatchCount, moduleContext, pageTypeContext, handleSportClick, observeVisibilityOptions, HiddenHorizontalScrollbar, PictureComp, CompetitionLogo, GlobalSportsV2Cards, getOverviewUrlBySport }
}

})